import { ReactNode } from 'react';
import { PrimaryButton, Spinner } from '@vertex-protocol/web-ui';
import { ButtonStateContent } from 'client/components/ButtonStateContent';
import { NadoDepositButtonState } from 'client/modules/collateral/nadoDeposit/types';

/** Bridge provider determines step labels and time estimates */
export type BridgeProviderLabel = 'oft' | 'cctp' | 'across' | 'stargate' | 'none';

interface Props {
  state: NadoDepositButtonState;
  chainName?: string;
  tokenSymbol?: string;
  isCrossChain?: boolean;
  bridgeDelivered?: boolean;
  bridgeProvider?: BridgeProviderLabel;
  isFundingAutomationAvailable?: boolean;
  isFundingAutomationActive?: boolean;
  onClick?: () => void;
}

/**
 * Multi-state submit button for the Nado deposit flow.
 * Labels adapt based on bridge provider:
 * - Across: 1/3 Bridge → 2/3 Approve → 3/3 Deposit (approve bundled)
 * - OFT/CCTP/Stargate: 1/4 Approve → 2/4 Bridge → 3/4 Approve → 4/4 Deposit
 * - Same-chain: 1/2 Approve → 2/2 Deposit
 */
export function NadoDepositSubmitButton({
  state,
  chainName = 'Chain',
  tokenSymbol = 'Token',
  isCrossChain = false,
  bridgeDelivered = false,
  bridgeProvider = 'oft',
  isFundingAutomationAvailable = false,
  isFundingAutomationActive = false,
  onClick,
}: Props) {
  const { label, isDisabled } = isFundingAutomationAvailable
    ? getFundingAutomationConfig(state, isFundingAutomationActive)
    : isCrossChain
      ? getCrossChainConfig(state, chainName, tokenSymbol, bridgeDelivered, bridgeProvider)
      : getSameChainConfig(state, chainName, tokenSymbol);

  return (
    <PrimaryButton
      disabled={isDisabled}
      onClick={onClick}
      type="submit"
      className="w-full"
    >
      {label}
    </PrimaryButton>
  );
}

function getFundingAutomationConfig(
  state: NadoDepositButtonState,
  isActive: boolean,
): ButtonConfig {
  switch (state) {
    case 'idle':
      return { label: 'Enter Amount', isDisabled: true };
    case 'route_unavailable':
      return { label: 'Route Unavailable', isDisabled: true };
    case 'wallet_mismatch':
      return { label: 'Reconnect Original Wallet', isDisabled: true };
    case 'partial_arrival':
      return { label: 'Continue With Received Amount', isDisabled: false };
    case 'arrival_timeout':
      return { label: 'Retry Arrival Check', isDisabled: false };
    case 'success':
      return { label: <ButtonStateContent.Success message="Deposit Complete" />, isDisabled: false };
    case 'quoting':
      return { label: <SpinnerLabel text="Preparing Deposit..." />, isDisabled: true };
    case 'switching_chain':
    case 'approving':
    case 'bridging':
    case 'bridge_submitted':
    case 'bridge_pending':
    case 'depositing':
      return { label: <SpinnerLabel text="Completing Deposit..." />, isDisabled: true };
    case 'requires_switch_chain':
    case 'requires_approve':
    case 'ready_bridge':
    case 'ready_deposit':
      return {
        label: isActive ? <SpinnerLabel text="Completing Deposit..." /> : 'Deposit',
        isDisabled: isActive,
      };
  }
}

function SpinnerLabel({ text }: { text: string }) {
  return (
    <span className="flex items-center justify-center gap-x-2">
      <Spinner size={16} className="fill-current" />
      <span>{text}</span>
    </span>
  );
}

interface ButtonConfig {
  label: ReactNode;
  isDisabled: boolean;
}

function getCrossChainConfig(
  state: NadoDepositButtonState,
  chainName: string,
  tokenSymbol: string,
  bridgeDelivered: boolean,
  provider: BridgeProviderLabel,
): ButtonConfig {
  // Across bundles approve+bridge: 1/3 Bridge → 2/3 Approve Nado → 3/3 Deposit
  // Others: 1/4 Approve → 2/4 Bridge → 3/4 Approve Nado → 4/4 Deposit
  const isAcross = provider === 'across';
  const total = isAcross ? 3 : 4;
  const bridgeStep = isAcross ? 1 : 2;
  const approveNadoStep = isAcross ? 2 : 3;
  const depositStep = isAcross ? 3 : 4;

  const providerName = provider === 'across' ? 'Across' : provider === 'cctp' ? 'CCTP' : 'LayerZero';

  switch (state) {
    case 'idle':
      return { label: 'Enter Amount', isDisabled: true };
    case 'requires_switch_chain':
      return { label: bridgeDelivered ? 'Switch to Ink' : `Switch to ${chainName}`, isDisabled: false };
    case 'switching_chain':
      return { label: <SpinnerLabel text="Switching..." />, isDisabled: true };
    case 'requires_approve':
      return {
        label: bridgeDelivered
          ? `${approveNadoStep}/${total}: Approve for Nado`
          : isAcross
            ? `${bridgeStep}/${total}: Bridge to Ink`
            : `1/${total}: Approve & Bridge`,
        isDisabled: false,
      };
    case 'approving':
      return {
        label: <SpinnerLabel text={
          bridgeDelivered
            ? `${approveNadoStep}/${total}: Approving...`
            : isAcross
              ? `${bridgeStep}/${total}: Bridging via ${providerName}...`
              : `1/${total}: Approving & Bridging...`
        } />,
        isDisabled: true,
      };
    case 'quoting':
      return { label: <SpinnerLabel text="Validating Across route..." />, isDisabled: true };
    case 'ready_bridge':
      return { label: `${bridgeStep}/${total}: Bridge to Ink`, isDisabled: false };
    case 'bridging':
      return { label: <SpinnerLabel text={`${bridgeStep}/${total}: Bridging via ${providerName}...`} />, isDisabled: true };
    case 'bridge_submitted':
      return { label: <SpinnerLabel text={`${bridgeStep}/${total}: Waiting for Ink arrival...`} />, isDisabled: true };
    case 'bridge_pending':
      return { label: <SpinnerLabel text={`${bridgeStep}/${total}: Waiting for bridge...`} />, isDisabled: true };
    case 'partial_arrival':
      return { label: 'Continue With Received Amount', isDisabled: false };
    case 'arrival_timeout':
      return { label: 'Retry Arrival Check', isDisabled: false };
    case 'route_unavailable':
      return { label: 'Route Unavailable', isDisabled: true };
    case 'wallet_mismatch':
      return { label: 'Reconnect Original Wallet', isDisabled: true };
    case 'ready_deposit':
      return { label: `${depositStep}/${total}: Deposit`, isDisabled: false };
    case 'depositing':
      return { label: <SpinnerLabel text={`${depositStep}/${total}: Depositing...`} />, isDisabled: true };
    case 'success':
      return { label: <ButtonStateContent.Success message="Deposit Complete" />, isDisabled: false };
  }
}

// Same-chain (Ink): 1/2 Approve → 2/2 Deposit
function getSameChainConfig(
  state: NadoDepositButtonState,
  chainName: string,
  tokenSymbol: string,
): ButtonConfig {
  switch (state) {
    case 'idle':
      return { label: 'Enter Amount', isDisabled: true };
    case 'requires_switch_chain':
      return { label: `Switch to ${chainName}`, isDisabled: false };
    case 'switching_chain':
      return { label: <SpinnerLabel text="Switching..." />, isDisabled: true };
    case 'requires_approve':
      return { label: `1/2: Approve ${tokenSymbol}`, isDisabled: false };
    case 'approving':
      return { label: <SpinnerLabel text="1/2: Approving..." />, isDisabled: true };
    case 'quoting':
      return { label: <SpinnerLabel text="Validating route..." />, isDisabled: true };
    case 'ready_deposit':
      return { label: '2/2: Deposit', isDisabled: false };
    case 'depositing':
      return { label: <SpinnerLabel text="2/2: Depositing..." />, isDisabled: true };
    case 'ready_bridge':
      return { label: 'Bridge & Deposit', isDisabled: false };
    case 'bridging':
      return { label: <SpinnerLabel text="Bridging..." />, isDisabled: true };
    case 'bridge_submitted':
      return { label: <SpinnerLabel text="Waiting for Ink arrival..." />, isDisabled: true };
    case 'bridge_pending':
      return { label: <SpinnerLabel text="Waiting for bridge..." />, isDisabled: true };
    case 'partial_arrival':
      return { label: 'Continue With Received Amount', isDisabled: false };
    case 'arrival_timeout':
      return { label: 'Retry Arrival Check', isDisabled: false };
    case 'route_unavailable':
      return { label: 'Route Unavailable', isDisabled: true };
    case 'wallet_mismatch':
      return { label: 'Reconnect Original Wallet', isDisabled: true };
    case 'success':
      return { label: <ButtonStateContent.Success message="Deposit Complete" />, isDisabled: false };
  }
}
